import React, { useMemo, useState } from 'react';
import { Box, Grid } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import DateRangeSelector from '../DateRangeSelector';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent } from '../hooks';
import InvoiceIDInput from '../InvoiceIDInput';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { ContributionInvoiceSummaryReportParams } from './ContributionInvoiceReport.types';
import {
  useContributionInvoiceReportExportSummaryCSVQuery,
  useContributionInvoiceReportSummaryQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ContributionInvoiceSummaryReportParams;
  onSetFilterValue: FilterSetters<ContributionInvoiceSummaryReportParams>;
}

const ContributionInvoiceReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const [isAutoFocus, setAutoFocus] = useState(false);

  const { isLoading, data, lastTimeUpdateDate, title, isEmptyData, isLoadingMore, isHasNextPage, handleLoadMore } =
    useContributionInvoiceReportSummaryQuery({
      ...filtersValues,
    });

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useContributionInvoiceReportExportSummaryCSVQuery({
      ...filtersValues,
    });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <ReportSummaryBase isPreviewMode={isPreviewMode} subTitle={title} isLoading={isLoading} data={data} onLoadMore={handleLoadMore} isHasMore={isHasNextPage} isLoadingMore={isLoadingMore} />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode, title]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            type={ReportTypeKeys.ContributionInvoice}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box fill>
              <Grid columns="1/3" gap="spacing8">
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />

                {!isPreviewMode && (
                  <DateRangeSelector
                    prefix="Date range"
                    startDate={filtersValues.startDate}
                    endDate={filtersValues.endDate}
                    onSetStartDate={onSetFilterValue.startDate}
                    onSetEndDate={onSetFilterValue.endDate}
                    disabled={!isEmployerIsPresent}
                  />
                )}

                <InvoiceIDInput
                  value={filtersValues.invoiceID}
                  disabled={!isEmployerIsPresent}
                  onChange={onSetFilterValue.invoiceID}
                />
              </Grid>
            </Box>
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default ContributionInvoiceReportSummary;
